// Firm onboarding: KPI tiles, filterable pipeline table (row menu: ⋯ or right-click) and "Invite a firm".
// A row opens the firm's own record page (FirmRecord.tsx).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Archive, ChevronRight, Eye, Link2, MoreHorizontal, Plus, Search, Trash2 } from 'lucide-react';
import {
  REVIEW_STEP, canTransition, optionLabel,
  type CreateOnboardingRequest, type DeleteOnboardingResponse, type LinkResponse, type OnboardingStatus, type PipelineRow, type Review,
} from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { fmtCalendarDate, fmtDate, fmtMonthDay, fmtRelative, fmtShortDay, fmtTime, zoneShort } from '../lib/format';
import { Avatar, Banner, Button, Dialog, Menu, Pill, Spinner, TableBox, Td, TextField, Th, cx, initialsOf, type MenuEntry } from '../ui';
import { ARCHIVED, useMe } from './AdminShell';
import { agreementPill, statusTone } from './labels';
import { LinkResult } from './LinkResult';
import { DeleteDialog, ReissueDialog, WithdrawDialog } from './Review';

const DAY = 86_400_000;
const WAITING: ReadonlySet<OnboardingStatus> = new Set(['invited', 'opened', 'in_progress']);
const NOT_SUBMITTED: ReadonlySet<OnboardingStatus> = new Set(['invited', 'opened', 'in_progress', 'expired']);

/**
 * Needs attention: an unopened invite whose link expires within 7 days, or a firm that hasn't
 * submitted and whose link has expired (status `expired`, or past linkExpiresAt).
 */
export function attention(r: PipelineRow, now: number): 'expiring' | 'expired' | null {
  if (!NOT_SUBMITTED.has(r.status)) return null;
  const exp = r.linkExpiresAt ? new Date(r.linkExpiresAt).getTime() : null;
  if (r.status === 'expired' || (exp !== null && exp <= now)) return 'expired';
  if (r.status === 'invited' && exp !== null && exp - now <= 7 * DAY) return 'expiring';
  return null;
}

/** 3 from the API's "In progress · step 3 of 6". */
const stepOf = (label: string) => Number(/step (\d+) of \d+/i.exec(label)?.[1] ?? 0) || null;

type Filter = 'all' | 'waiting' | 'review' | 'archived';
const FILTERS: Array<{ id: Filter; label: string; test: (r: PipelineRow) => boolean }> = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'waiting', label: 'Waiting on firm', test: (r) => WAITING.has(r.status) },
  { id: 'review', label: 'Ready for review', test: (r) => r.status === 'submitted' },
  { id: 'archived', label: 'Archived', test: (r) => ARCHIVED.has(r.status) },
];

/** A row menu's Withdraw, Reissue link or Delete: the dialog runs on the firm's review, loaded with ?peek=1. */
type RowAction = { kind: 'withdraw' | 'reissue' | 'delete'; review: Review };

const joinParts = (parts: Array<[number, string]>) => parts.filter(([n]) => n > 0).map(([n, t]) => `${n} ${t}`).join(' · ');

/** What the firm list says after a delete, here or on the firm's record page (passed in the navigation state). */
export type DeletedState = { deleted: { firmName: string; result: DeleteOnboardingResponse } };

export function OnboardingPage() {
  const { reportPipeline } = useMe();
  const location = useLocation();
  const [rows, setRows] = useState<PipelineRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  // A deleted firm's Discovery Session that Outlook wouldn't drop: staff cancel it there.
  const [staleSession, setStaleSession] = useState<string | null>(null);
  // `anchor`: the ⋯ button that opened it (null for right-click); `returnFocus`: that row's ⋯ either way.
  const [menu, setMenu] = useState<{ row: PipelineRow; x: number; y: number; anchor: HTMLElement | null; returnFocus: HTMLElement | null } | null>(null);
  const [action, setAction] = useState<RowAction | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const r = await api.get<PipelineRow[]>('/api/admin/onboardings'); setRows(r); reportPipeline(r); setError(null); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not load the pipeline.'); }
  }, [reportPipeline]);
  useEffect(() => { void load(); }, [load]);

  const afterDelete = (name: string, result: DeleteOnboardingResponse) => {
    setNotice(`${name} was deleted.`);
    setStaleSession(result.session && !result.session.cancelledInOutlook ? result.session.startsAt : null);
    void load();
    window.scrollTo?.({ top: 0 });
  };
  // A delete on the record page comes back here with its result: show it once, then clear it from history.
  useEffect(() => {
    const d = (location.state as DeletedState | null)?.deleted;
    if (!d) return;
    afterDelete(d.firmName, d.result);
    navigate('.', { replace: true, state: null });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Only the latest row action opens its dialog: an earlier, slower load is dropped.
  const actionSeq = useRef(0);
  const startAction = async (kind: RowAction['kind'], row: PipelineRow) => {
    const seq = ++actionSeq.current;
    setNotice(null);
    setStaleSession(null);
    try {
      const review = await api.get<Review>(`/api/admin/onboardings/${row.id}?peek=1`);
      if (seq === actionSeq.current) setAction({ kind, review });
    } catch (e) { if (seq === actionSeq.current) setError(e instanceof ApiError ? e.message : 'Could not load this onboarding.'); }
  };
  const closeAction = () => setAction(null);

  const rowEntries = (r: PipelineRow): MenuEntry[] => {
    const manage: MenuEntry[] = [
      ...(!ARCHIVED.has(r.status) ? [{ label: 'Reissue link…', icon: <Link2 size={14} />, onSelect: () => void startAction('reissue', r) }] : []),
      ...(canTransition(r.status, 'withdrawn') ? [{ label: 'Withdraw and archive…', icon: <Archive size={14} />, danger: true, onSelect: () => void startAction('withdraw', r) }] : []),
      // Only once withdrawn, so deleting always takes two deliberate steps; never once the firm has signed.
      ...(r.status === 'withdrawn' ? [r.firmSigned
        ? { label: 'Signed: kept for the record', icon: <Trash2 size={14} />, disabled: true }
        : { label: 'Delete onboarding…', icon: <Trash2 size={14} />, danger: true, onSelect: () => void startAction('delete', r) }] : []),
    ];
    return [
      { kind: 'header', label: r.firmName },
      { label: 'Open review', icon: <ChevronRight size={14} />, onSelect: () => navigate(`/admin/onboarding/${r.id}`) },
      { label: 'Preview as firm', icon: <Eye size={14} />, onSelect: () => navigate(`/admin/onboarding/${r.id}/preview`) },
      ...(manage.length ? [{ kind: 'separator' } as const, ...manage] : []),
    ];
  };
  const closeMenu = useCallback(() => { menu?.returnFocus?.focus(); setMenu(null); }, [menu]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const f = FILTERS.find((x) => x.id === filter)!;
    const q = query.trim().toLowerCase();
    return rows.filter((r) => f.test(r) && (!q || r.firmName.toLowerCase().includes(q) || r.contactName.toLowerCase().includes(q)));
  }, [rows, filter, query]);

  return (
    <>
      <div className="-mb-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="m-0 text-[22px] font-bold leading-tight tracking-[-0.01em]">Firm onboarding</h1>
        <Button variant="primary" size="sm" onClick={() => setInviting(true)}>
          <Plus aria-hidden="true" size={16} strokeWidth={2} />Invite a firm
        </Button>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {notice && <Banner tone="success" role="status">{notice}</Banner>}
      {staleSession && (
        <Banner tone="warn" role="alert">
          Its Discovery Session on {fmtDate(staleSession)} couldn’t be removed from Outlook. Cancel the meeting there so the firm isn’t left with it.
        </Banner>
      )}
      {!rows && !error && <Spinner label="Loading firms" />}
      {rows && <KpiTiles rows={rows} filter={filter} onFilter={setFilter} />}
      {rows && rows.length === 0 && (
        <div className="flex flex-col items-start gap-3 rounded-2xl border border-line bg-white px-6 py-8 shadow-card">
          <h2 className="card-title">No firms yet</h2>
          <p className="m-0 text-muted">Invite a firm to send the first onboarding link. It shows up here as soon as it’s created.</p>
          <Button variant="primary" onClick={() => setInviting(true)}><Plus aria-hidden="true" size={18} />Invite a firm</Button>
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-4 py-2.5">
            <div role="group" aria-label="Filter firms" className="flex max-w-full gap-1 overflow-x-auto rounded-[10px] bg-subtle p-1">
              {FILTERS.map((f) => {
                const n = rows.filter(f.test).length;
                const on = filter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setFilter(f.id)}
                    className={cx('min-h-8 whitespace-nowrap rounded-lg border-0 px-3 text-[13px]', on ? 'bg-white font-semibold text-ink shadow-seg' : 'bg-transparent font-medium text-ink-2 hover:text-ink')}
                  >
                    {f.label}{n > 0 && <span className={cx('ml-1.5', on ? 'text-ink' : 'text-muted')}>{n}</span>}
                  </button>
                );
              })}
            </div>
            <label className="flex h-9 min-w-0 flex-[0_1_260px] items-center gap-2 rounded-[10px] border border-line-strong px-3 text-muted focus-within:outline focus-within:outline-[3px] focus-within:outline-offset-2 focus-within:outline-brand-ring">
              <Search aria-hidden="true" size={16} strokeWidth={2} className="flex-none" />
              <span className="sr-only">Search firms</span>
              <input
                type="search"
                placeholder="Search firms"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-full w-full min-w-0 border-0 bg-transparent text-sm text-ink outline-none focus-visible:outline-none"
              />
            </label>
          </div>
          <div aria-live="polite" className="sr-only">{`${shown.length} ${shown.length === 1 ? 'firm' : 'firms'} shown`}</div>
          <TableBox bare minWidth={900} label="Firms in onboarding">
            <thead>
              <tr>
                <Th className="!py-2">Firm</Th><Th className="!py-2">Stage</Th><Th className="!py-2">Clients</Th>
                <Th className="!py-2">Go-live</Th><Th className="!py-2">Agreement</Th>
                <Th className="!py-2">Discovery Session</Th><Th className="!py-2">Last activity</Th>
                <Th className="!py-2"><span className="sr-only">Actions</span></Th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr className="border-t border-line-soft"><Td colSpan={8} className="!py-6 text-muted">No firms match. Try another filter or search.</Td></tr>
              )}
              {shown.map((r) => (
                <PipelineTableRow key={r.id} r={r} menuOpen={menu?.row.id === r.id}
                  onMenu={(x, y, anchor, returnFocus) => setMenu(anchor && menu?.row.id === r.id ? null : { row: r, x, y, anchor, returnFocus })} />
              ))}
            </tbody>
          </TableBox>
        </div>
      )}
      {menu && <Menu x={menu.x} y={menu.y} anchor={menu.anchor} label={`Actions for ${menu.row.firmName}`} entries={rowEntries(menu.row)} onClose={closeMenu} />}
      {action?.kind === 'withdraw' && (
        <WithdrawDialog open r={action.review} onClose={closeAction}
          onWithdrawn={async (n) => { closeAction(); setNotice(`${action.review.header.firmName}: ${n}`); await load(); }} />
      )}
      {action?.kind === 'reissue' && <ReissueDialog open r={action.review} onClose={closeAction} onDone={load} />}
      {action?.kind === 'delete' && (
        <DeleteDialog open r={action.review} onClose={closeAction} onDeleted={(res) => { closeAction(); afterDelete(action.review.header.firmName, res); }} />
      )}
      <InviteDialog open={inviting} onClose={() => setInviting(false)} onCreated={load} />
    </>
  );
}

/** A compact count: label, number and one line of detail. With `onClick` it filters the table. */
function Kpi({ label, value, sub, warn, onClick, pressed }: { label: string; value: number; sub: string; warn?: boolean; onClick?: () => void; pressed?: boolean }) {
  const body = (
    <>
      <span className="flex items-baseline gap-2">
        <span className={cx('text-xl font-bold leading-none tabular-nums', warn && value > 0 && 'text-warn')}>{value}</span>
        <span className="text-[13px] font-semibold text-ink-2">{label}</span>
      </span>
      <span className="truncate text-xs text-muted" title={sub}>{sub}</span>
    </>
  );
  const cls = cx('flex min-w-0 flex-col gap-1 rounded-card border bg-white px-3.5 py-2.5 text-left', pressed ? 'border-brand bg-brand-tint' : 'border-line');
  return onClick
    ? <button type="button" aria-pressed={pressed} onClick={onClick} className={cx(cls, 'cursor-pointer font-[inherit] hover:border-brand')}>{body}</button>
    : <div className={cls}>{body}</div>;
}

function KpiTiles({ rows, filter, onFilter }: { rows: PipelineRow[]; filter: Filter; onFilter: (f: Filter) => void }) {
  const toggle = (f: Filter) => onFilter(filter === f ? 'all' : f);
  const now = Date.now();
  const waiting = rows.filter((r) => WAITING.has(r.status));
  const count = (s: OnboardingStatus) => waiting.filter((r) => r.status === s).length;
  const review = rows.filter((r) => r.status === 'submitted');
  const booked = rows.filter((r) => r.kickoffAt && new Date(r.kickoffAt).getTime() > now).sort((a, b) => a.kickoffAt!.localeCompare(b.kickoffAt!));
  const flagged = rows.map((r) => ({ r, a: attention(r, now) })).filter((x) => x.a !== null);
  const expiring = flagged.filter((x) => x.a === 'expiring');
  const expired = flagged.filter((x) => x.a === 'expired');
  const live = rows.filter((r) => !ARCHIVED.has(r.status));
  // A firm ticked as signed by hand isn't waiting, though its in-app agreement may still be 'sent'.
  const withFirm = live.filter((r) => r.agreementStatus === 'sent' && !r.firmSigned).length;
  const toCountersign = live.filter((r) => r.agreementStatus === 'firm_signed').length;

  const attentionSub = (() => {
    if (flagged.length === 0) return 'No links expiring this week';
    if (flagged.length === 1) {
      const { r, a } = flagged[0]!;
      if (a === 'expired') return `${r.firmName}: link expired`;
      const days = Math.max(0, Math.ceil((new Date(r.linkExpiresAt!).getTime() - now) / DAY));
      return `${r.firmName}: link expires ${days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`}, unopened`;
    }
    return joinParts([[expiring.length, 'expiring this week, unopened'], [expired.length, 'expired']]);
  })();

  return (
    <div className="grid grid-cols-1 gap-2.5 min-[360px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <Kpi
        label="Waiting on the firm"
        value={waiting.length}
        sub={waiting.length ? joinParts([[count('invited'), 'not opened'], [count('opened'), 'opened'], [count('in_progress'), 'in progress']]) : 'No open invites'}
        onClick={() => toggle('waiting')}
        pressed={filter === 'waiting'}
      />
      <Kpi
        label="Ready for review"
        value={review.length}
        sub={review.length === 0 ? 'Nothing waiting for review' : review.length === 1 ? `${review[0]!.firmName} submitted` : `${review[0]!.firmName} and ${review.length - 1} more`}
        onClick={() => toggle('review')}
        pressed={filter === 'review'}
      />
      <Kpi
        label="Discovery Sessions booked"
        value={booked.length}
        sub={booked[0] ? `Next: ${fmtShortDay(booked[0].kickoffAt!)} · ${fmtTime(booked[0].kickoffAt!)}` : 'None upcoming'}
      />
      <Kpi
        label="Agreements out"
        value={withFirm + toCountersign}
        sub={joinParts([[withFirm, 'with the firm'], [toCountersign, 'to countersign']]) || 'None waiting on a signature'}
      />
      <Kpi label="Needs attention" value={flagged.length} sub={attentionSub} warn />
    </div>
  );
}

function StageCell({ r }: { r: PipelineRow }) {
  const step = r.status === 'in_progress' ? stepOf(r.stageLabel) : null;
  const label = r.status === 'invited' ? 'Not opened' : r.status === 'submitted' ? 'Ready for review' : step ? `Step ${step} of ${REVIEW_STEP}` : r.stageLabel;
  const expires = NOT_SUBMITTED.has(r.status) && r.status !== 'expired' && r.linkExpiresAt ? r.linkExpiresAt : null;
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <Pill tone={statusTone(r.status)} dot className="!py-0.5">{label}</Pill>
      {expires && <span className={cx('whitespace-nowrap text-xs', r.status === 'invited' ? 'text-warn-fg' : 'text-muted')}>Link expires {fmtMonthDay(expires)}</span>}
    </div>
  );
}

/** "Before tax season" over the target date when they gave one (the review's "Wants to go live" tile). */
function GoLiveCell({ r }: { r: PipelineRow }) {
  const main = optionLabel('targetTiming', r.targetTiming) ?? (r.targetDate ? fmtCalendarDate(r.targetDate) : null);
  if (!main) return <span className="text-muted">—</span>;
  return (
    <div className="flex flex-col whitespace-nowrap leading-tight">
      <span>{main}</span>
      {r.targetTiming && r.targetDate && <span className="text-xs text-muted">{fmtCalendarDate(r.targetDate)}</span>}
    </div>
  );
}


function AgreementCell({ r }: { r: PipelineRow }) {
  const p = agreementPill(r.agreementStatus, r.firmSigned);
  if (!p) return <span className="text-muted">—</span>;
  return <span title={p.title}><Pill tone={p.tone} className="whitespace-nowrap !py-0.5">{p.label}</Pill></span>;
}

const cell = '!px-4 !py-2 align-middle';

function PipelineTableRow({ r, menuOpen, onMenu }: {
  r: PipelineRow; menuOpen: boolean;
  onMenu: (x: number, y: number, anchor: HTMLElement | null, returnFocus: HTMLElement | null) => void;
}) {
  const navigate = useNavigate();
  const rel = fmtRelative(r.lastActivityAt);
  const activity = r.status === 'invited' ? `Invited ${/^\d|^[A-Z][a-z]{2} \d/.test(rel) ? rel : rel.toLowerCase()}` : rel;
  return (
    <tr
      className="cursor-pointer border-t border-line-soft hover:bg-row-hi"
      onClick={() => navigate(`/admin/onboarding/${r.id}`)}
      // Right-click opens the row menu; the firm name link keeps the browser's own menu (open in a new tab).
      onContextMenu={(e) => {
        if ((e.target as HTMLElement).closest('a')) return;
        e.preventDefault();
        onMenu(e.clientX, e.clientY, null, e.currentTarget.querySelector<HTMLElement>('button[aria-haspopup="menu"]'));
      }}
    >
      <Td className={cell}>
        <div className="flex items-center gap-2.5">
          <Avatar initials={initialsOf(r.firmName, 'firm')} size={28} tone="soft" square />
          <div className="flex min-w-0 flex-col leading-tight">
            <Link to={`/admin/onboarding/${r.id}`} className="font-semibold text-ink no-underline hover:underline" onClick={(e) => e.stopPropagation()}>{r.firmName}</Link>
            <span className="text-xs text-muted">{r.contactName}</span>
          </div>
        </div>
      </Td>
      <Td className={cell}><StageCell r={r} /></Td>
      <Td className={cx(cell, 'tabular-nums', r.clients == null && 'text-muted')}>{r.clients ?? '—'}</Td>
      <Td className={cell}><GoLiveCell r={r} /></Td>
      <Td className={cell}><AgreementCell r={r} /></Td>
      <Td className={cx(cell, 'whitespace-nowrap')}>
        {r.kickoffAt
          ? <><span className="font-semibold">{fmtShortDay(r.kickoffAt)}</span> <span className="text-muted">· {fmtTime(r.kickoffAt)} {zoneShort(new Date(r.kickoffAt))}</span></>
          : <span className="text-muted">Not booked</span>}
      </Td>
      <Td className={cx(cell, 'whitespace-nowrap text-muted')}>{activity}</Td>
      <Td className={cx(cell, '!py-1 !pl-0 !pr-2')}>
        <button
          type="button"
          aria-label={`Actions for ${r.firmName}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            const b = e.currentTarget.getBoundingClientRect();
            onMenu(Math.max(8, b.right - 230), b.bottom + 4, e.currentTarget, e.currentTarget);
          }}
          className={cx('grid h-8 w-8 place-items-center rounded-lg border-0 text-muted hover:bg-subtle hover:text-ink', menuOpen ? 'bg-subtle text-ink' : 'bg-transparent')}
        >
          <MoreHorizontal aria-hidden="true" size={18} strokeWidth={2} />
        </button>
      </Td>
    </tr>
  );
}

export function InviteDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => Promise<void> }) {
  const { me } = useMe();
  const connected = !!me?.graph.connected;
  const [form, setForm] = useState<CreateOnboardingRequest>({ firmName: '', contactName: '', contactEmail: '', send: true });
  const systemMailbox = me?.systemMailbox ?? 'no-reply@vertekal.com';
  const [result, setResult] = useState<LinkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An open onboarding already has this contact email: staff can still create a second one on purpose.
  const [duplicate, setDuplicate] = useState(false);
  // Enter in a field submits through the hidden button, so `busy` alone can't stop a second request.
  const inFlight = useRef(false);
  const navigate = useNavigate();

  useEffect(() => { if (open) { setForm({ firmName: '', contactName: '', contactEmail: '', send: true }); setResult(null); setError(null); setDuplicate(false); } }, [open]);

  const valid = form.firmName.trim() && form.contactName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim());
  const submit = async (allowDuplicate = false) => {
    if (inFlight.current) return;
    if (!valid) { setError('Enter the firm name, contact name and a valid email.'); return; }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setDuplicate(false);
    try {
      const res = await api.post<LinkResponse>('/api/admin/onboardings', {
        ...form, firmName: form.firmName.trim(), contactName: form.contactName.trim(), contactEmail: form.contactEmail.trim(),
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      });
      setResult(res);
      await onCreated();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'duplicate_onboarding') {
        setDuplicate(true);
        setError(e.message);
      } else if (e instanceof ApiError && e.code !== 'network' && e.status < 500) {
        setError(e.message);
      } else {
        // The invite may exist even though no answer came back. A retry with the same email is refused as a duplicate.
        setError('We couldn’t confirm the invite was created. Check the pipeline for this firm before trying again. If it’s there, use Reissue link on its review.');
        await onCreated().catch(() => undefined);
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const methodClass = (on: boolean) => cx('flex cursor-pointer items-start gap-2.5 rounded-[10px] px-3.5 py-3 text-sm', on ? 'border-[1.5px] border-brand bg-brand-tint' : 'border border-line-btn bg-white');
  return (
    <Dialog
      open={open}
      title={!result ? 'Invite a firm' : result.emailed ? 'Invite created' : `Invite ready for ${form.firmName.trim()}`}
      wide={!!result?.draft}
      onClose={busy ? () => undefined : onClose}
      footer={result ? (
        <>
          <Button onClick={() => { onClose(); navigate(`/admin/onboarding/${result.id}`); }}>Open review</Button>
          <Button variant="primary" onClick={onClose}>Done</Button>
        </>
      ) : (
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>{form.send ? 'Create and email invite' : 'Create invite'}</Button>
        </>
      )}
    >
      {result ? <LinkResult result={result} email={form.send ? form.contactEmail : null} systemMailbox={systemMailbox} /> : (
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <TextField label="Firm name" value={form.firmName} onChange={(e) => setForm({ ...form, firmName: e.target.value })} required />
          <TextField label="Contact name" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} required />
          <TextField label="Contact email" type="email" value={form.contactEmail} onChange={(e) => { setForm({ ...form, contactEmail: e.target.value }); setDuplicate(false); }} required />
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-1.5 p-0 text-sm font-semibold">How should the invite go out?</legend>
            <label className={methodClass(form.send)}>
              <input type="radio" name="invite-method" className="mt-0.5 h-[18px] w-[18px] flex-none accent-brand" checked={form.send} onChange={() => setForm({ ...form, send: true })} />
              <span>
                <span className="block font-semibold">Email it for me</span>
                <span className="block text-[13px] text-muted">
                  {connected ? 'Sends a formatted email from your Outlook.' : `Sends a formatted email from ${systemMailbox}, signed by you, with replies to you. Connect Outlook in Settings to send from your own mailbox.`}
                </span>
              </span>
            </label>
            <label className={methodClass(!form.send)}>
              <input type="radio" name="invite-method" className="mt-0.5 h-[18px] w-[18px] flex-none accent-brand" checked={!form.send} onChange={() => setForm({ ...form, send: false })} />
              <span>
                <span className="block font-semibold">I’ll send it myself</span>
                <span className="block text-[13px] text-muted">Copy a plain-text message and link into a new Outlook email. Use this when a firm’s email filters block formatted mail.</span>
              </span>
            </label>
          </fieldset>
          {duplicate ? (
            <Banner tone="warn" role="alert">
              <span className="block">{error} Open it from the pipeline to reissue its link, or create a second onboarding if this is a different firm.</span>
              <Button variant="outline" size="sm" className="mt-2" disabled={busy} onClick={() => void submit(true)}>Create anyway</Button>
            </Banner>
          ) : error && <Banner tone="error" role="alert">{error}</Banner>}
          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}
